import { useState } from "react";
import { useAuth } from "@/AuthContext";
import { createBusiness, uploadLogo, mediaUrl } from "@/api";
import { PlatformLayout } from "./Layout";
import { LoyaltyBrandingFields, DEFAULT_CONFIG, splitConfig } from "./LoyaltyBrandingFields";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, ArrowRight, ArrowLeft, CheckCircle2, Upload, Store } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return "Etwas ist schiefgelaufen.";
}

export const OnboardingWizard = () => {
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [cfg, setCfg] = useState({ ...DEFAULT_CONFIG });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const create = async () => {
    setBusy(true); setError("");
    try {
      const { branding, loyalty } = splitConfig(cfg);
      await createBusiness({ name: name.trim(), location: location.trim(), branding, loyalty });
      setCreated(true);
      setStep(4);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadLogo(file);
      setLogoUrl(res.logo_url);
      toast.success("Logo hochgeladen ✓");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const finish = async () => { await refreshUser(); };

  return (
    <PlatformLayout user={{}} logout={() => {}} subtitle="Einrichtung">
      <div className="max-w-lg mx-auto pt-2 animate-float-up">
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "bg-[var(--bs-teal)]" : "bg-slate-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <Card title="Dein Betrieb" subtitle="Wie heißt dein Geschäft?">
            <Field label="Name des Betriebs">
              <Input data-testid="onb-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Café Mokka" className="h-12 rounded-xl" />
            </Field>
            <Field label="Standort (optional)">
              <Input data-testid="onb-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z. B. Berlin-Mitte" className="h-12 rounded-xl" />
            </Field>
            <Button data-testid="onb-next-1" disabled={name.trim().length < 2} onClick={() => setStep(2)}
              className="w-full py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press disabled:opacity-50">
              Weiter <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
          </Card>
        )}

        {step === 2 && (
          <Card title="Treueprogramm" subtitle="Wähle dein Modell und die Regeln.">
            <LoyaltyBrandingFields value={cfg} onChange={setCfg} idPrefix="onb" />
            <div className="flex gap-2">
              <Button data-testid="onb-back-2" variant="outline" onClick={() => setStep(1)} className="flex-1 py-3.5 rounded-2xl font-bold"><ArrowLeft className="mr-1 h-5 w-5" /> Zurück</Button>
              <Button data-testid="onb-next-2" onClick={() => setStep(3)} className="flex-1 py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press">Weiter <ArrowRight className="ml-1 h-5 w-5" /></Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card title="Fast fertig" subtitle="Prüfe deine Angaben und lege los.">
            <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-4 space-y-2 text-sm">
              <Row k="Betrieb" v={name} />
              {location && <Row k="Standort" v={location} />}
              <Row k="Modell" v={cfg.mode === "stamp_fixed" ? "Stempel pro Einkauf" : cfg.mode === "stamp_spend" ? "Stempel nach Umsatz" : "Punkte nach Umsatz"} />
              <Row k="Belohnung" v={cfg.reward_title} />
              <div className="flex items-center gap-2 pt-1">
                <span className="h-6 w-6 rounded-full ring-2 ring-white" style={{ background: cfg.primary_color }} />
                <span className="h-6 w-6 rounded-full ring-2 ring-white" style={{ background: cfg.secondary_color }} />
                <span className="text-xs text-slate-500 font-semibold">Markenfarben</span>
              </div>
            </div>
            {error && <p data-testid="onb-error" className="text-sm font-semibold text-rose-600 bg-rose-50 rounded-xl px-3 py-2">{error}</p>}
            <div className="flex gap-2">
              <Button data-testid="onb-back-3" variant="outline" onClick={() => setStep(2)} className="flex-1 py-3.5 rounded-2xl font-bold"><ArrowLeft className="mr-1 h-5 w-5" /> Zurück</Button>
              <Button data-testid="onb-create-button" onClick={create} disabled={busy} className="flex-1 py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press disabled:opacity-60">
                {busy ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Erstellen…</> : <>Betrieb erstellen</>}
              </Button>
            </div>
          </Card>
        )}

        {step === 4 && created && (
          <Card title="Betrieb erstellt! 🎉" subtitle="Optional: lade jetzt dein Logo hoch.">
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="h-24 w-24 rounded-3xl bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center overflow-hidden">
                {logoUrl ? <img src={mediaUrl(logoUrl)} alt="Logo" className="h-full w-full object-contain" /> : <Store className="h-10 w-10 text-slate-300" />}
              </div>
              <label data-testid="onb-logo-upload" className="cursor-pointer inline-flex items-center gap-2 rounded-xl bg-slate-100 hover:bg-slate-200 px-4 py-2.5 font-bold text-sm text-slate-700 press">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {logoUrl ? "Logo ersetzen" : "Logo hochladen"}
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={uploading} />
              </label>
            </div>
            <Button data-testid="onb-finish-button" onClick={finish} className="w-full py-3.5 rounded-2xl bg-[var(--bs-teal)] hover:bg-[var(--bs-teal-hover)] font-display font-bold press">
              <CheckCircle2 className="mr-1 h-5 w-5" /> Zum Dashboard
            </Button>
          </Card>
        )}
      </div>
    </PlatformLayout>
  );
};

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-3xl ring-1 ring-slate-100 shadow-soft p-6 space-y-4">
    <div>
      <h1 className="font-display font-extrabold text-2xl text-[var(--bs-ink)]">{title}</h1>
      {subtitle && <p className="text-slate-500 text-sm font-medium mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div className="space-y-1.5"><label className="text-xs font-bold text-slate-500">{label}</label>{children}</div>
);
const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3"><span className="text-slate-400 font-semibold">{k}</span><span className="text-slate-800 font-bold text-right">{v}</span></div>
);
